import { data, useActionData } from 'react-router'
import {
	BookDemoTabbedForm,
	GridBackdrop,
} from '#app/components/book-demo-tabbed-form.tsx'
import { MarketingPageShell } from '#app/components/marketing-page-shell.tsx'
import { Reveal } from '#app/components/reveal.tsx'
import {
	createWebPageSchema,
	organizationSchema,
	webSiteSchema,
} from '#app/utils/constants.ts'
import { processSalesLeadSubmission } from '#app/utils/marketing-form.server.ts'
import { type Route } from './+types/book-demo.ts'

const MetaTitle = 'Book a Demo | DealerProEdge'
const MetaDescription =
	'Talk with DealerProEdge about faster, SEO-ready dealership websites, inventory pages, and lead generation.'
const directScheduleNamespace = 'book-demo-direct'

export const handle = { chrome: 'payload' }

const discussionPoints = [
	'How your current website is performing across SEO, speed, and lead flow',
	'What a DealerProEdge build would look like for your inventory and market',
	'Where shoppers drop off before they become qualified opportunities',
	'Timeline, migration path, and the right first package for your team',
]

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/book-demo'
	const canonicalUrl = `${origin}${path}`
	const pageSchema = createWebPageSchema({
		name: MetaTitle,
		description: MetaDescription,
		url: canonicalUrl,
	})

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': organizationSchema },
		{ 'script:ld+json': webSiteSchema },
		{ 'script:ld+json': pageSchema },
	]
}

export async function action(args: Route.ActionArgs) {
	const formData = await args.request.formData()
	const result = await processSalesLeadSubmission(formData, 'book-demo', args)

	if (!result.success) {
		return data(
			{ result: result.submission },
			{
				status: result.status,
				headers: 'headers' in result ? result.headers : undefined,
			},
		)
	}

	return data({ success: true })
}

export default function BookDemoPage() {
	const actionData = useActionData<typeof action>()
	const didSubmit = actionData && 'success' in actionData && actionData.success
	const lastResult =
		actionData && 'result' in actionData ? actionData.result : undefined

	return (
		<MarketingPageShell>
			<div className="bg-[#f7f7f2] text-[#101010]">
				<section className="relative overflow-hidden border-b border-black/10">
					<GridBackdrop />
					<div className="mx-auto grid min-h-[calc(100vh-150px)] max-w-[1440px] grid-cols-1 px-5 py-14 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:px-10 lg:py-20">
						<Reveal className="relative order-1 flex flex-col items-center justify-center border-black/10 pb-10 pt-10 text-center lg:order-1 lg:border-r lg:pr-14 lg:text-left">
							<h1 className="max-w-[640px] text-[clamp(2.25rem,4.4vw,4rem)] font-medium leading-[1.02] text-[#101010]">
								Schedule a Demo
							</h1>
							<div className="mt-10 hidden max-w-xl lg:block">
								<h2 className="text-base font-medium text-[#101010] md:text-lg">
									What we'll talk about:
								</h2>
								<ul className="mt-3 space-y-1 text-base font-semibold leading-6 text-[#101010] md:text-lg">
									{discussionPoints.map((point) => (
										<li key={point} className="flex items-center gap-3">
											<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-black" />
											<span>{point}</span>
										</li>
									))}
								</ul>
							</div>
						</Reveal>

						<Reveal
							delay={0.08}
							className="relative order-2 flex items-center pt-10 lg:order-2 lg:pl-14 lg:pt-0"
						>
							<div className="w-full">
								<BookDemoTabbedForm
									isSubmitted={Boolean(didSubmit)}
									lastResult={lastResult}
									scheduleNamespace={directScheduleNamespace}
									usePairedFieldRows
								/>
							</div>
						</Reveal>

						<Reveal className="order-3 mt-16 max-w-xl px-5 pb-10 sm:px-8 lg:hidden">
							<h2 className="text-base font-medium text-[#101010] md:text-lg">
								What we'll talk about:
							</h2>
							<ul className="mt-3 space-y-1 text-base font-semibold leading-6 text-[#101010] md:text-lg">
								{discussionPoints.map((point) => (
									<li key={point} className="flex items-center gap-3">
										<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-black" />
										<span>{point}</span>
									</li>
								))}
							</ul>
						</Reveal>
					</div>
				</section>
			</div>
		</MarketingPageShell>
	)
}
